/** Layout tablette / téléphone — presets calés sur site.txt (config mobile de référence). */

import presets from '../data/jardinsMobilePresets.json';
import { refineJardinsSectionMobile } from './jardinsMobileRefine.js';

function siteUsesJardinsKit(site) {
  return (site?.sections || []).some((s) => s.designKit === 'jardins');
}

export const JARDINS_MOBILE_LAYOUT_V = presets.version || 2;

function resolveSectionPresetKey(section) {
  const tone = section?.kitSectionTone;
  if (tone === 'announcement') return 'announcement';
  if (tone === 'header') return 'header';
  if (tone === 'hero-dark') return 'hero-dark';
  if (tone === 'forest') return 'forest';
  if (tone === 'quote') return 'quote';
  if (tone === 'gallery-pad') return 'gallery-pad';
  if (tone === 'reservation') return 'reservation';
  if (tone === 'footer') return 'footer';
  const anchor = section?.anchor;
  if (anchor === 'histoire' || section?.id === 'sec-j-intro') return 'intro';
  if (anchor === 'plats' || section?.id === 'sec-j-plats') return 'plats';
  if (anchor === 'infos' || section?.id === 'sec-j-infos') return 'infos';
  if (anchor === 'carte' || section?.id === 'sec-j-saison') return 'forest';
  return null;
}

function presetMatchKey(comp) {
  return `${Number(comp.layout) || 0}|${comp.props?.kitStyle || ''}|${comp.type || ''}`;
}

function buildPresetLookup(presetSection) {
  const map = new Map();
  for (const p of presetSection?.components || []) {
    const key = `${p.layout}|${p.kitStyle || ''}|${p.type || ''}`;
    if (!map.has(key)) map.set(key, p);
  }
  return map;
}

function applyPlacement(comp, viewport, placement) {
  if (!placement) return comp;
  const prev = comp[viewport] || {};
  const next = {
    colStart: placement.colStart ?? 1,
    colSpan: placement.colSpan ?? 48,
    row: placement.row ?? 0,
    rowSpan: placement.rowSpan ?? 1,
  };
  if (placement.hidden) {
    return { ...comp, [viewport]: { ...prev, hidden: true } };
  }
  const merged = { ...prev, ...next };
  delete merged.hidden;
  return { ...comp, [viewport]: merged };
}

export function stackJardinsSectionResponsive(section) {
  if (!section?.designKit || section.designKit !== 'jardins') return section;

  const presetKey = resolveSectionPresetKey(section);
  const presetSection = presetKey ? presets.sections?.[presetKey] : null;
  if (!presetSection) return section;

  if (presetKey === 'header') {
    const components = (section.components || []).map((comp) => {
      const kit = comp.props?.kitStyle;
      if (kit === 'jardins-nav-link' || kit === 'jardins-nav-cta') {
        return {
          ...comp,
          mobile: { ...(comp.mobile || {}), hidden: true },
          tablet: { ...(comp.tablet || {}), hidden: true },
        };
      }
      return comp;
    });
    return {
      ...section,
      components,
      gridRows: Number(section.gridRows) || 0,
      jardinsMobileGridRows: 5,
      jardinsTabletGridRows: 5,
    };
  }

  const lookup = buildPresetLookup(presetSection);

  const components = (section.components || []).map((comp) => {
    const key = presetMatchKey(comp);
    const preset = lookup.get(key);
    if (!preset) return comp;
    let next = comp;
    if (preset.mobile) {
      next = applyPlacement(next, 'mobile', preset.mobile);
    }
    if (preset.tablet) {
      next = applyPlacement(next, 'tablet', preset.tablet);
    }
    return next;
  });

  const desktopGridRows = Number(section.gridRows) || 0;
  const mobileGridRows = Number(presetSection.mobileGridRows) || desktopGridRows;
  const tabletGridRows = Number(presetSection.tabletGridRows) || desktopGridRows;

  return refineJardinsSectionMobile(
    {
      ...section,
      components,
      gridRows: desktopGridRows,
      gridRowsCustom: section.gridRowsCustom !== false,
      jardinsMobileGridRows: mobileGridRows,
      jardinsTabletGridRows: tabletGridRows,
    },
    presetKey,
  );
}

export function applyJardinsResponsiveToSite(site) {
  if (!siteUsesJardinsKit(site)) return site;
  const sections = (site.sections || []).map(stackJardinsSectionResponsive);
  return {
    ...site,
    sections,
    jardinsMobileLayoutV: JARDINS_MOBILE_LAYOUT_V,
  };
}

function sectionHasStoredViewportLayout(sec) {
  if (!sec || typeof sec !== 'object') return false;
  if (Number(sec.jardinsMobileGridRows) > 0 || Number(sec.jardinsTabletGridRows) > 0) {
    return true;
  }
  if (Number(sec.mobileGridRows) > 0 || Number(sec.tabletGridRows) > 0) {
    return true;
  }
  if (sec.mobile?.hidden === true || sec.tablet?.hidden === true) {
    return true;
  }
  return (sec.components || []).some((c) => {
    const m = c.mobile;
    const t = c.tablet;
    if (!m && !t) return false;
    if (m?.hidden || t?.hidden) return true;
    return (
      m?.colStart != null ||
      m?.row != null ||
      m?.colSpan != null ||
      m?.rowSpan != null ||
      t?.colStart != null ||
      t?.row != null ||
      t?.colSpan != null ||
      t?.rowSpan != null
    );
  });
}

export function siteHasStoredMobileLayout(site) {
  return (site?.sections || []).some(sectionHasStoredViewportLayout);
}

/** Réapplique les presets mobile une seule fois (sans écraser un layout déjà édité). */
export function ensureJardinsResponsiveLayout(site) {
  if (!siteUsesJardinsKit(site)) return site;
  if ((site.jardinsMobileLayoutV || 0) >= JARDINS_MOBILE_LAYOUT_V) return site;
  if (siteHasStoredMobileLayout(site)) {
    return { ...site, jardinsMobileLayoutV: JARDINS_MOBILE_LAYOUT_V };
  }
  return applyJardinsResponsiveToSite(site);
}

export function isJardinsHeaderSection(section) {
  return section?.id === 'sec-j-header' || section?.kitSectionTone === 'header';
}

/** Menu compact (hamburger) — header uniquement, pas la grille empilée. */
export function shouldUseJardinsCompactHeader(section, viewport) {
  return (
    viewport !== 'desktop' &&
    isJardinsHeaderSection(section) &&
    section?.designKit === 'jardins'
  );
}

export { resolveJardinsSectionPadding } from './jardinsMobileRefine.js';

export function resolveJardinsViewportGridRows(section, viewport) {
  if (viewport === 'mobile' && Number(section?.jardinsMobileGridRows) > 0) {
    return Number(section.jardinsMobileGridRows);
  }
  if (viewport === 'tablet' && Number(section?.jardinsTabletGridRows) > 0) {
    return Number(section.jardinsTabletGridRows);
  }
  return 0;
}
